import { isGroup, type Condition, type FormField, type Rule } from '@re/form-engine';
import type { Lookups } from '@re/form-ui';

/**
 * "Who sees this field" — most conditions in real forms are one of three simple filters:
 * request type ∈ {…}, category ∈ {…}, property type ∈ {…} (joined with AND). This module turns a
 * rule into those three sets (plus whatever else it contains, kept untouched) and back, so the
 * builder can offer chips and a matrix instead of the raw rule editor. Same rule format as before.
 */
export type Dim = 'request_type' | 'property_category' | 'property_type';
export const DIMS: Dim[] = ['request_type', 'property_category', 'property_type'];

export interface Quick {
  /** allowed codes per dimension; undefined = no filter on that dimension */
  sets: Partial<Record<Dim, string[]>>;
  /** every other condition of the AND list, kept as is */
  rest: Rule[];
}

/** The field (before `index`) each dimension reads from. */
export function dimFields(fields: FormField[], index: number): Partial<Record<Dim, FormField>> {
  const out: Partial<Record<Dim, FormField>> = {};
  for (const f of fields.slice(0, Math.max(0, index))) {
    const t = f.type as Dim;
    if (DIMS.includes(t) && !out[t]) out[t] = f;
  }
  return out;
}

export function universe(dim: Dim, lookups?: Lookups): string[] {
  if (!lookups) return [];
  if (dim === 'request_type') return lookups.requestTypes.map((r) => r.code);
  if (dim === 'property_category') return lookups.categories.map((r) => r.code);
  return lookups.propertyTypes.map((r) => r.code);
}

const asList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : v === undefined || v === null || v === '' ? [] : [String(v)]);

/** null when the rule is not an AND list (e.g. a top-level OR) — then only the advanced editor applies. */
export function parseQuick(rule: Rule | null | undefined, dims: Partial<Record<Dim, FormField>>, lookups?: Lookups): Quick | null {
  if (!rule) return { sets: {}, rest: [] };
  let items: Rule[];
  if (isGroup(rule)) {
    if (rule.any || rule.not || !rule.all) {
      // an OR with a single item is the same as that item
      if (rule.any && rule.any.length === 1 && !rule.all && !rule.not) items = rule.any;
      else return null;
    } else items = rule.all;
  } else items = [rule];

  const q: Quick = { sets: {}, rest: [] };
  for (const it of items) {
    const c = it as Condition;
    const dim = !isGroup(it) ? DIMS.find((d) => dims[d]?.key === c.field) : undefined;
    if (dim && q.sets[dim] === undefined && ['in', 'not_in', 'eq', 'neq'].includes(c.op)) {
      const vals = asList(c.value);
      const all = universe(dim, lookups);
      q.sets[dim] = c.op === 'in' || c.op === 'eq' ? all.filter((x) => vals.includes(x)).concat(vals.filter((x) => !all.includes(x))) : all.filter((x) => !vals.includes(x));
    } else q.rest.push(it);
  }
  return q;
}

/** Back to a rule: the shorter list wins (`not_in` keeps future lookup rows visible). */
export function buildRule(q: Quick, dims: Partial<Record<Dim, FormField>>, lookups?: Lookups): Rule | null {
  const conds: Rule[] = [];
  for (const d of DIMS) {
    const set = q.sets[d];
    const src = dims[d];
    if (set === undefined || !src) continue;
    const all = universe(d, lookups);
    const included = all.filter((x) => set.includes(x));
    const excluded = all.filter((x) => !set.includes(x));
    if (excluded.length === 0 && set.every((x) => all.includes(x))) continue; // everyone → no filter
    if (excluded.length > 0 && excluded.length < included.length) conds.push({ field: src.key, op: 'not_in', value: excluded });
    else conds.push({ field: src.key, op: 'in', value: set });
  }
  const list = [...conds, ...q.rest];
  if (!list.length) return null;
  if (list.length === 1) return list[0]!;
  return { all: list };
}

/** Advanced part of the rule (everything except the three filters) as one rule for RuleEditor. */
export const restRule = (rest: Rule[]): Rule | null => (rest.length === 0 ? null : rest.length === 1 ? rest[0]! : { all: rest });
export const fromRestRule = (r: Rule | null): Rule[] => (!r ? [] : isGroup(r) && r.all && !r.any && !r.not ? r.all : [r]);

/** Is the field visible for this code of the dimension, ignoring the other filters? null = can't tell (advanced rule). */
export function visibleFor(f: FormField, dim: Dim, code: string, dims: Partial<Record<Dim, FormField>>, lookups?: Lookups): boolean | null {
  const q = parseQuick(f.visibleWhen, dims, lookups);
  if (!q) return null;
  const set = q.sets[dim];
  return set === undefined ? true : set.includes(code);
}

/** Toggle one code of one dimension in the field's visibility. */
export function toggleVisible(f: FormField, dim: Dim, code: string, on: boolean, dims: Partial<Record<Dim, FormField>>, lookups?: Lookups): FormField {
  const q = parseQuick(f.visibleWhen, dims, lookups);
  if (!q) return f;
  const cur = q.sets[dim] ?? universe(dim, lookups);
  const next = on ? Array.from(new Set([...cur, code])) : cur.filter((x) => x !== code);
  return { ...f, visibleWhen: buildRule({ ...q, sets: { ...q.sets, [dim]: next } }, dims, lookups) };
}
